import type { Placement } from '@popperjs/core';
import { InfoTooltip } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isUndefined } from 'lodash';
import React, { useState } from 'react';
import { usePopper } from 'react-popper';

const { info, tooltip, tooltipArrow } = InfoTooltip.styles;

interface Props {
  content: React.ReactNode;
  refElementClassName?: string;
  tooltipClassName?: string;
  arrowClassName?: string;
  hideTooltip?: boolean;
  placement?: Placement;
  isOpen?: boolean;
}

type TooltipElement = HTMLDivElement | null;

const Tooltip: React.FC<React.PropsWithChildren<Props>> = ({
  content,
  children,
  refElementClassName,
  arrowClassName,
  tooltipClassName,
  hideTooltip,
  placement,
  isOpen
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [referenceElement, setReferenceElement] = useState<TooltipElement>(null);
  const [popperElement, setPopperElement] = useState<TooltipElement>(null);
  const [arrowElement, setArrowElement] = useState<TooltipElement>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      { name: 'offset', options: { offset: [0, 15] } }
    ]
  });

  return (
    <>
      <div
        className={classNames(info, refElementClassName)}
        ref={setReferenceElement}
        onMouseLeave={() => isUndefined(isOpen) && setShowTooltip(false)}
        onMouseOver={() => !hideTooltip && setShowTooltip(true)}
      >
        {children}
      </div>
      {(isOpen || showTooltip) && (
        <div
          className={classNames(tooltip, tooltipClassName)}
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          {content}
          <div
            className={classNames(tooltipArrow, arrowClassName)}
            ref={setArrowElement}
            style={styles.arrow}
          />
        </div>
      )}
    </>
  );
};

export default Tooltip;
